import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from 'src/app/services';
import { MatDialog } from '@angular/material/dialog';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { UtilsService } from 'src/app/services/utils.service';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  EmployeeRequestResponsibleForAnswerStatusEnum,
  EmployeeRequestResponsibleForAnswerStatusLabels,
} from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { WorkingExtraHoursPeriod } from 'src/app/models/working-extra-hours-period.model';
import { CommonModule, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WorkingExtraHoursPeriodDialogComponent } from './working-extra-hours-period-dialog/working-extra-hours-period-dialog.component';
import { EmployeeRequestApprovalStatusDialogComponent } from 'src/app/components/main/shared/employee-request-approval-status-dialog/employee-request-approval-status-dialog.component';
import { EmployeeRequestResponsibleForAnswersRequestableTypeEnum } from 'src/app/enums/employee-request-responsible-for-answers-requestable-type.enum';
import { UserService } from 'src/app/services/user.service';
import { EmployeeRequestApprovalStatusDialogData } from 'src/app/models/employee-request-responsible-for-answer.model';

@Component({
  selector: 'app-my-working-extra-hours-periods',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    DatePipe,
  ],
  templateUrl: './my-working-extra-hours-periods.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./my-working-extra-hours-periods.component.css'],
})
export class MyWorkingExtraHoursPeriodsComponent extends IndexTemplateComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private api = inject(ApiService);
  private dialog = inject(MatDialog);
  protected fb: UntypedFormBuilder;
  protected router: Router;
  protected activatedRoute: ActivatedRoute;
  protected utils: UtilsService;
  private userService = inject(UserService);

  readonly EmployeeRequestResponsibleForAnswerStatusEnum = EmployeeRequestResponsibleForAnswerStatusEnum;

  displayedColumns: string[] = [
    'start_date',
    'end_date',
    'max_weekly_hours',
    'company_product',
    'is_remote_working_allowed',
    'justification',
    'status',
    'actions',
  ];
  dataSource = new MatTableDataSource<WorkingExtraHoursPeriod>();

  override filterFormExtraParams: any = {
    status: [''],
  };

  constructor() {
    const fb = inject(UntypedFormBuilder);
    const router = inject(Router);
    const activatedRoute = inject(ActivatedRoute);
    const utils = inject(UtilsService);

    super(router, fb, activatedRoute, utils);

    this.fb = fb;
    this.router = router;
    this.activatedRoute = activatedRoute;
    this.utils = utils;
  }

  override fetchData(): void {
    const user = this.userService.getCurrentUser();
    if (!user) return;

    const params: any = {
      ...this.filterForm.value,
      user_id: user.id,
      with: 'company_products',
      order_by: 'created_at:desc',
    };

    this.api.getWorkingExtraHoursPeriods(params).subscribe({
      next: data => {
        this.dataSource.data = data.data;
        this.setMetadata(data.meta.total, data.meta.current_page - 1, data.meta.per_page);
      },
    });
  }

  openDialog(): void {
    const dialogRef = this.dialog.open(WorkingExtraHoursPeriodDialogComponent, {
      width: '600px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.fetchData();
      }
    });
  }

  getStatusLabel(status: EmployeeRequestResponsibleForAnswerStatusEnum): string {
    return EmployeeRequestResponsibleForAnswerStatusLabels[status] ?? status;
  }

  getStatusClass(status: EmployeeRequestResponsibleForAnswerStatusEnum): string {
    switch (status) {
      case EmployeeRequestResponsibleForAnswerStatusEnum.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED:
        return 'bg-green-100 text-green-800';
      case EmployeeRequestResponsibleForAnswerStatusEnum.REJECTED:
        return 'bg-red-100 text-red-800';
      default:
        return '';
    }
  }

  formatCompanyProducts(element: WorkingExtraHoursPeriod): string {
    const titles = element.company_products?.map(company_product => company_product.title) ?? [];

    return titles.length > 0 ? titles.join(', ') : '—';
  }

  delete(id: number): void {
    if (confirm('¿Estás seguro de que deseas eliminar esta solicitud?')) {
      this.api.deleteWorkingExtraHoursPeriod(id).subscribe({
        next: () => this.fetchData(),
        error: err => console.error(err),
      });
    }
  }

  openApprovalStatusDialog(working_extra_hours_period: WorkingExtraHoursPeriod): void {
    this.dialog.open(EmployeeRequestApprovalStatusDialogComponent, {
      width: '800px',
      data: {
        requestable_type: EmployeeRequestResponsibleForAnswersRequestableTypeEnum.WorkingExtraHoursPeriod,
        requestable_id: working_extra_hours_period.id,
        requestable_status: working_extra_hours_period.status,
      } as EmployeeRequestApprovalStatusDialogData,
    });
  }
}
